import { Component } from "../lib/component.ts";

const DESKTOP_QUERY = "(width >= 768px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Resolves multiple CSS custom properties to pixel values in a single DOM operation.
 */
function resolveCSSVars(...varNames: string[]): number[] {
  const el = document.createElement("div");
  el.style.position = "absolute";
  el.style.visibility = "hidden";
  document.body.appendChild(el);

  const values = varNames.map((varName) => {
    el.style.height = `var(${varName})`;
    return parseFloat(getComputedStyle(el).height) || 0;
  });

  el.remove();
  return values;
}

/**
 * Product highlight section with scroll-based object-position.
 * Sets --scroll-progress (0-1) as user scrolls through the section.
 * Only active on desktop viewports.
 */
class ProductHighlightLarge extends Component {
  #frameId = 0;
  #resizeTimeout = 0;
  #isVisible = false;
  #isActive = false;
  #buyBoxHeight = 0;
  #desktopQuery = window.matchMedia(DESKTOP_QUERY);
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

  connectedCallback(): void {
    super.connectedCallback();

    this.#desktopQuery.addEventListener("change", this.#handleMediaChange, {
      signal: this.signal,
    });
    this.#reducedMotionQuery.addEventListener("change", this.#handleMediaChange, {
      signal: this.signal,
    });

    this.#handleMediaChange();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #handleMediaChange = (): void => {
    const shouldBeActive = this.#desktopQuery.matches && !this.#reducedMotionQuery.matches;

    if (shouldBeActive && !this.#isActive) {
      this.#activate();
    } else if (!shouldBeActive && this.#isActive) {
      this.#deactivate();
    }

    if (this.#reducedMotionQuery.matches) {
      this.style.setProperty("--scroll-progress", "0.5");
    }
  };

  #activate(): void {
    this.#isActive = true;
    this.#updateCSSVars();
    this.#observeVisibility();
    this.#bindEvents();
  }

  #deactivate(): void {
    this.#isActive = false;
    this.#cleanup();
    this.style.removeProperty("--scroll-progress");
  }

  #cleanup(): void {
    if (this.#frameId) {
      cancelAnimationFrame(this.#frameId);
      this.#frameId = 0;
    }
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = 0;
    }
  }

  #updateCSSVars(): void {
    [this.#buyBoxHeight] = resolveCSSVars("--buy-box-height-sticky");
  }

  #observeVisibility(): void {
    const observer = new IntersectionObserver(
      (entries) => {
        this.#isVisible = entries[0]?.isIntersecting ?? false;
        if (this.#isVisible) {
          this.#updatePosition();
        }
      },
      { rootMargin: "100px" },
    );

    observer.observe(this);
    this.signal.addEventListener("abort", () => observer.disconnect());
  }

  #bindEvents(): void {
    window.addEventListener("scroll", this.#scheduleUpdate, {
      passive: true,
      signal: this.signal,
    });
    window.addEventListener("resize", this.#handleResize, {
      passive: true,
      signal: this.signal,
    });
  }

  #handleResize = (): void => {
    if (this.#resizeTimeout) clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = window.setTimeout(() => {
      this.#resizeTimeout = 0;
      this.#updateCSSVars();
      this.#updatePosition();
    }, 100);
  };

  #scheduleUpdate = (): void => {
    if (this.#frameId || !this.#isVisible) return;
    this.#frameId = requestAnimationFrame(this.#updatePosition);
  };

  #updatePosition = (): void => {
    this.#frameId = 0;

    const rect = this.getBoundingClientRect();
    const visibleHeight = window.innerHeight - this.#buyBoxHeight;
    const scrollDistance = rect.height - visibleHeight;

    if (scrollDistance <= 0) return;

    const scrolled = -rect.top;
    const progress = Math.max(0, Math.min(1, scrolled / scrollDistance));

    this.style.setProperty("--scroll-progress", String(progress));
  };
}

if (!customElements.get("product-highlight-large")) {
  customElements.define("product-highlight-large", ProductHighlightLarge);
}
